import { maskitoTransform } from '@maskito/core'
import { MaskitoDirective } from '@maskito/angular';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { mask_porcentagem_limpa } from '../../directives/mascaras';
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import { NotEmptyInteger } from "../../directives/not-empty.directive";
import { MaxFourDirective } from "../../directives/max-four.directive";
import { NoLeftZeroDirective } from "../../directives/no-left-zero.directive";
import { OnlyNumbersDirective } from "../../directives/only-numbers.directive";
import { AcessoOfertaEducacionalStateService } from "../../services";
import { FormsModule } from '@angular/forms';
import { ProjecaoOfertaMatriculas } from '../../dtos';
import { NgIf } from '@angular/common';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { EtapasEnsino } from '../../enums/etapas-ensino.enum';

interface ElementoTabela {
  totalDiurnoENoturno: number;
  diurno: number;
  totalIntegral?: number;
  porcentagemIntegral?: string;
  noturno?: number;
}

interface Tabela {
  creche: ElementoTabela;
  crecheMenor1Ano: ElementoTabela;
  creche1Ano: ElementoTabela;
  creche2Anos: ElementoTabela;
  creche3Anos: ElementoTabela;
  preEscola: ElementoTabela;
  ensinoFundamentalIniciais: ElementoTabela;
  ensinoFundamentalFinais: ElementoTabela;
  ensinoMedio: ElementoTabela;
  eja: ElementoTabela;
}

@Component({
  selector: 'app-escola',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NotEmptyInteger,
    TabelaComponent,
    MaxFourDirective,
    MaskitoDirective,
    NoLeftZeroDirective,
    OnlyNumbersDirective,
    FormsModule
  ],
  templateUrl: './escola.component.html',
  styleUrl: './escola.component.scss'
})
export class EscolaComponent implements OnInit, OnChanges {
  @Input() projecao: ProjecaoOfertaMatriculas | undefined;

  // #1128: estado do accordion controlado pelo pai (abrir "um por vez")
  @Input() expandido = false;
  @Output() expandidoChange = new EventEmitter<boolean>();

  public mask_porcentagem_limpa = mask_porcentagem_limpa;

  public nome: string;
  public tabela: Tabela;
  public exibirCrechePorIdade = false;

  // calcula tabela apenas ao expandir
  private tabelaCalculada = false;

  constructor(
    private acessoOfertaEducacionalStateService: AcessoOfertaEducacionalStateService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.nome = '';
    this.exibirCrechePorIdade = this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade');

    this.tabela = {
      creche: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      crecheMenor1Ano: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      creche1Ano: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      creche2Anos: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      creche3Anos: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      preEscola: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      ensinoFundamentalIniciais: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      ensinoFundamentalFinais: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      ensinoMedio: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      },
      eja: {
        totalDiurnoENoturno: 0,
        diurno: 0,
        totalIntegral: 0,
        porcentagemIntegral: '0',
        noturno: 0
      }
    }
  }

  ngOnInit(): void {
    this.nome = this.projecao?.nomeInstituicao ?? '';

    if (this.expandido) {
      this.calcularTabela();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandido'] && this.expandido) {
      this.calcularTabela();
    }
  }

  public alternarExpansao(): void {
    this.expandidoChange.emit(!this.expandido);
  }

  // calcula a tabela apenas na primeira vez que a escola é expandida
  private calcularTabela(): void {
    if (this.tabelaCalculada) return;
    this.tabelaCalculada = true;

    this.atribuirMatricula('creche', 1);
    this.atribuirMatricula('preEscola', 2);
    this.atribuirMatricula('ensinoFundamentalIniciais', 3);
    this.atribuirMatricula('ensinoFundamentalFinais', 4);
    this.atribuirMatricula('ensinoMedio', 5);
    this.atribuirMatricula('eja', 6);

    this.calcularSecao('creche', [1]);
    this.calcularSecao('preEscola', [2]);
    this.calcularSecao('ensinoFundamentalIniciais', [3]);
    this.calcularSecao('ensinoFundamentalFinais', [4]);
    this.calcularSecao('ensinoMedio', [5]);
    this.calcularSecao('eja', [6]);

    if (this.exibirCrechePorIdade) {
      this.atribuirMatricula('crecheMenor1Ano', EtapasEnsino.CRECHE_MENOR_1_ANO);
      this.atribuirMatricula('creche1Ano', EtapasEnsino.CRECHE_1_ANO);
      this.atribuirMatricula('creche2Anos', EtapasEnsino.CRECHE_2_ANOS);
      this.atribuirMatricula('creche3Anos', EtapasEnsino.CRECHE_3_ANOS);

      this.calcularSecao('crecheMenor1Ano', [EtapasEnsino.CRECHE_MENOR_1_ANO]);
      this.calcularSecao('creche1Ano', [EtapasEnsino.CRECHE_1_ANO]);
      this.calcularSecao('creche2Anos', [EtapasEnsino.CRECHE_2_ANOS]);
      this.calcularSecao('creche3Anos', [EtapasEnsino.CRECHE_3_ANOS]);

      this.recalcularCrecheAgregada();
    }
  }

  public atribuirMatricula(etapa: keyof Tabela, codigo: number): void {
    const e = this.projecao?.etapasEnsino.find(x => x.codEtapaEnsino === codigo);
    const diurnoParcial = e?.numeroMatriculasDiurno ?? 0;
    const integral = e?.numeroMatriculasIntegral ?? 0;
    const noturno = e?.numeroMatriculasNoturno ?? 0;

    const diurnoTotal = diurnoParcial + integral;

    this.tabela[etapa].diurno = diurnoTotal;
    this.tabela[etapa].noturno = noturno;
    this.tabela[etapa].totalDiurnoENoturno = diurnoTotal + noturno;
    this.tabela[etapa].totalIntegral = integral;

    const porcentagem = diurnoTotal > 0 ? (integral / diurnoTotal) * 100 : 0;
    this.tabela[etapa].porcentagemIntegral = maskitoTransform(String(porcentagem), this.mask_porcentagem_limpa);
  }

  public calcularSecao(secao: keyof Tabela, codigos: number[]): void {
    let diurnoTotal = 0, noturno = 0, integral = 0;

    for (const codigo of codigos) {
      const e = this.projecao?.etapasEnsino.find(x => x.codEtapaEnsino === codigo);

      if (!e) continue;

      const parcial = e.numeroMatriculasDiurno ?? 0;
      const integ = e.numeroMatriculasIntegral ?? 0;
      diurnoTotal += parcial + integ;
      noturno += e.numeroMatriculasNoturno ?? 0;
      integral += integ;
    }

    this.tabela[secao].diurno = diurnoTotal;
    this.tabela[secao].noturno = noturno;
    this.tabela[secao].totalDiurnoENoturno = diurnoTotal + noturno;
    this.tabela[secao].totalIntegral = Math.round(integral);
    const porcentagem = diurnoTotal > 0 ? (integral / diurnoTotal) * 100 : 0;
    this.tabela[secao].porcentagemIntegral = maskitoTransform(String(porcentagem), this.mask_porcentagem_limpa);
  }


  public atualizarValores(): void {
    this.atualizarTabela();
    this.atualizarProjecao();
  }

  public atualizarProjecao(): void {
    if (this.exibirCrechePorIdade) {
      this.atualizarSecao('crecheMenor1Ano', [EtapasEnsino.CRECHE_MENOR_1_ANO]);
      this.atualizarSecao('creche1Ano', [EtapasEnsino.CRECHE_1_ANO]);
      this.atualizarSecao('creche2Anos', [EtapasEnsino.CRECHE_2_ANOS]);
      this.atualizarSecao('creche3Anos', [EtapasEnsino.CRECHE_3_ANOS]);
    }

    this.atualizarSecao('creche', [1]);
    this.atualizarSecao('preEscola', [2]);
    this.atualizarSecao('ensinoFundamentalIniciais', [3]);
    this.atualizarSecao('ensinoFundamentalFinais', [4]);
    this.atualizarSecao('ensinoMedio', [5]);
    this.atualizarSecao('eja', [6]);

    this.acessoOfertaEducacionalStateService.updateProjecao(this.projecao!);
  }

  public atualizarTabela() {
    if (this.exibirCrechePorIdade) {
      this.recalcularElementoCreche('crecheMenor1Ano');
      this.recalcularElementoCreche('creche1Ano');
      this.recalcularElementoCreche('creche2Anos');
      this.recalcularElementoCreche('creche3Anos');
      this.recalcularCrecheAgregada();
    } else {
      this.tabela.creche.diurno =
        Number(this.tabela.creche.diurno);

      this.tabela.creche.totalDiurnoENoturno =
        Number(this.tabela.creche.diurno) +
        Number(this.tabela.creche.noturno);

      this.tabela.creche.totalIntegral =
        Number(((
          Number(this.tabela.creche.totalDiurnoENoturno) *
          Number(this.tabela.creche.porcentagemIntegral!.replace(',', '.'))
        ) / 100).toFixed(0));
    }

    this.tabela.preEscola.totalDiurnoENoturno = Number(this.tabela.preEscola.diurno);
    this.tabela.preEscola.totalIntegral =
      Number(((
        Number(this.tabela.preEscola.totalDiurnoENoturno) *
        Number(this.tabela.preEscola.porcentagemIntegral!.replace(',', '.'))
      ) / 100).toFixed(0));

    this.tabela.ensinoFundamentalIniciais.totalDiurnoENoturno = Number(this.tabela.ensinoFundamentalIniciais.diurno) + Number(this.tabela.ensinoFundamentalIniciais.noturno || 0);
    this.tabela.ensinoFundamentalIniciais.totalIntegral =
      Number(((
        Number(this.tabela.ensinoFundamentalIniciais.diurno) *
        Number(this.tabela.ensinoFundamentalIniciais.porcentagemIntegral!.replace(',', '.'))
      ) / 100).toFixed(0));

    this.tabela.ensinoFundamentalFinais.totalDiurnoENoturno = Number(this.tabela.ensinoFundamentalFinais.diurno) + Number(this.tabela.ensinoFundamentalFinais.noturno || 0);
    this.tabela.ensinoFundamentalFinais.totalIntegral =
      Number(((
        Number(this.tabela.ensinoFundamentalFinais.diurno) *
        Number(this.tabela.ensinoFundamentalFinais.porcentagemIntegral!.replace(',', '.'))
      ) / 100).toFixed(0));

    this.tabela.ensinoMedio.totalDiurnoENoturno = Number(this.tabela.ensinoMedio.diurno) + Number(this.tabela.ensinoMedio.noturno!);
    this.tabela.ensinoMedio.totalIntegral =
      Number(((
        Number(this.tabela.ensinoMedio.diurno) *
        Number(this.tabela.ensinoMedio.porcentagemIntegral!.replace(',', '.'))
      ) / 100).toFixed(0));

    this.tabela.eja.totalDiurnoENoturno = Number(this.tabela.eja.diurno) + Number(this.tabela.eja.noturno!);
  }

  public atualizarSecao(secao: keyof Tabela, codigos: number[]): void {
    if (!this.projecao) return;

    const codigo = codigos[0];
    let etapa = this.projecao.etapasEnsino.find(e => e.codEtapaEnsino === codigo);

    if (!etapa) {
      etapa = {
        codEtapaEnsino: codigo,
        descricao: '',
        numeroMatriculasDiurno: 0,
        numeroMatriculasNoturno: 0,
        numeroMatriculasIntegral: 0,
      };
      this.projecao.etapasEnsino.push(etapa);
    }

    const diurnoTotal = Number(this.tabela[secao].diurno) || 0;
    const noturno = Number(this.tabela[secao].noturno) || 0;
    const pct = Number(String(this.tabela[secao].porcentagemIntegral ?? '0').replace(',', '.')) || 0;

    const integral = Math.round(diurnoTotal * (pct / 100));
    const diurnoParcial = Math.max(0, diurnoTotal - integral);

    etapa.numeroMatriculasDiurno = diurnoParcial;
    etapa.numeroMatriculasIntegral = integral;
    etapa.numeroMatriculasNoturno = noturno;
  }

  // #1128: recalcula os totais de um segmento de creche a partir do diurno e do % editados
  private recalcularElementoCreche(secao: keyof Tabela): void {
    const elemento = this.tabela[secao];

    elemento.diurno = Number(elemento.diurno);
    elemento.totalDiurnoENoturno = Number(elemento.diurno) + Number(elemento.noturno ?? 0);
    elemento.totalIntegral =
      Number(((
        Number(elemento.totalDiurnoENoturno) *
        Number(String(elemento.porcentagemIntegral ?? '0').replace(',', '.'))
      ) / 100).toFixed(0));
  }

  // #1128: a linha "Creche" agregada e' somente leitura e vale a soma dos 4 segmentos por idade
  private recalcularCrecheAgregada(): void {
    const segmentos = [
      this.tabela.crecheMenor1Ano,
      this.tabela.creche1Ano,
      this.tabela.creche2Anos,
      this.tabela.creche3Anos,
    ];

    const diurno = segmentos.reduce((total, s) => total + Number(s.diurno ?? 0), 0);
    const integral = segmentos.reduce((total, s) => total + Number(s.totalIntegral ?? 0), 0);
    const noturno = segmentos.reduce((total, s) => total + Number(s.noturno ?? 0), 0);

    this.tabela.creche.diurno = diurno;
    this.tabela.creche.noturno = noturno;
    this.tabela.creche.totalIntegral = integral;
    this.tabela.creche.totalDiurnoENoturno = diurno + noturno;

    const porcentagem = diurno > 0 ? (integral / diurno) * 100 : 0;
    this.tabela.creche.porcentagemIntegral = maskitoTransform(String(porcentagem), this.mask_porcentagem_limpa);
  }


  public atualizarMatricula(etapa: keyof Tabela, codigo: number): void {
    if (this.projecao?.etapasEnsino.find(e => e.codEtapaEnsino === codigo)) {
      this.projecao.etapasEnsino.find(e => e.codEtapaEnsino === codigo)!.numeroMatriculasDiurno = Number(this.tabela[etapa].diurno);
      return;
    }

    this.projecao?.etapasEnsino.push({
      codEtapaEnsino: codigo,
      numeroMatriculasDiurno: Number(this.tabela[etapa].diurno),
      numeroMatriculasIntegral: 0,
      numeroMatriculasNoturno: 0,
      descricao: ''
    });
  }
}
